// What this app has written and not yet seen the server answer for (#167).
//
// ── Why a record at all, when the SDK already has a queue ───────────────────
//
// Firestore's own mutation queue is durable — it is in IndexedDB, that is what
// `persistentLocalCache` buys — but the **callbacks are not**. The SDK says so
// itself, in `processUserCallback`: "Mutations restored from persistence won't
// have callbacks, so it's okay for there to be no callback for this ID." So a
// write queued offline, a reload, then a reconnect that refuses it rejects
// nothing: `src/data/writes.js` never hears, and without this the chip would
// say "Saved" for an edit that was gone (#167).
//
// This file is the durable half the callbacks are missing: one small record per
// intended write, outliving the tab, cleared when the write is answered.
//
// ── Why it is also the pending count (#169, #170) ───────────────────────────
//
// Summing `hasPendingWrites` over `limit()`ed listeners on a list of
// collections would miss a collection not on the list (#169), and a `limit(N)`
// listener is **blind** to a pending write on a document outside its window
// (#170, test/integration/pendingWriteReach.test.jsx). A record written here
// covers every collection, has no window, and needs no listeners.
//
// ── Why localStorage, and why the write happens before the mutation ─────────
//
// **`localStorage` is synchronous.** `setItem` has completed by the time the
// next statement runs, so a record written immediately before the SDK call
// survives the tab being closed immediately after it. IndexedDB's writes are
// asynchronous, and their tail is what a closing tab loses.
//
// It is also shared across tabs of one origin: a write made in one tab is
// outstanding for the studio, and the `storage` event tells the other tabs.
//
// The cost is a quota (~5 MB) and that `setItem` can throw — in private
// browsing, or with site data blocked. Both degrade to an in-memory map, whose
// records do not outlive the tab.

const KEY_PREFIX = 'coda.outstandingWrite.';

// A record is small: paths and a noun, not the data being written — **except
// for the few milliseconds before the write queue has its own record** (#585).
//
// ── Why a note carries a copy of its batch, briefly ─────────────────────────
//
// This note is written synchronously and the queue's record asynchronously
// (`writes.js` notes, then awaits an IndexedDB put), so a page that goes away
// between the two leaves a note with nothing behind it. A note naming only
// paths would leave that write gone: nothing could send it, and a later page's
// #167 check would report it as answered with nobody listening (#585).
//
// So the note carries `batch` — the queue's own `{ ops, fields, subject }`,
// already encoded by `opCodec.js` and therefore plain JSON — **until the queue
// says its record is durable**, and then `releaseBatchCopy` drops it. On a
// browser where the queue is memory-only (#208's degraded store) it is kept
// until the batch settles, because there the note is the only thing that would
// survive a reload. `writes.js`'s `recoverUnqueuedWrites` is what reads it.
//
// Data is otherwise kept out of here, which is why the copy is dropped: a note
// that outlives the session should not carry a family's details once the
// queue holds the write.
const MAX_RECORDS = 500;

let memoryFallback = null;
let probed = false;
const listeners = new Set();

// **Probed once, not per call.** Writing and deleting a probe key on every read
// would fire a `storage` event in every other tab each time the chip asked for
// its count — a loop across tabs.
function store() {
  if (memoryFallback) return null;
  try {
    const s = globalThis.localStorage;
    if (!probed) {
      // Touch it rather than trusting its presence: a browser with site data
      // blocked exposes the object and throws on write.
      const probe = `${KEY_PREFIX}probe`;
      s.setItem(probe, '1');
      s.removeItem(probe);
      probed = true;
    }
    return s;
  } catch {
    // One line, once. A teacher in a private window should not get a console
    // full of this, and the degraded behaviour is explained where it is chosen.
    if (!memoryFallback) {
      memoryFallback = new Map();
      console.warn(
        '[sync] no usable localStorage; outstanding writes are tracked in memory only, '
        + 'so a reload will forget them (#167).',
      );
    }
    return null;
  }
}

function announce() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('[sync] outstanding-write listener threw:', err);
    }
  });
}

/**
 * Note that a write has been issued, and hand back the id that clears it.
 *
 * **Call this before issuing the write, not after**, so the record survives a
 * tab that dies between the two. `localStorage.setItem` is synchronous, so on return the record is
 * durable.
 */
export function recordWrite({
  paths = [], subject = null, batchId = null, batch = null, followOn = null,
} = {}) {
  const id = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 10)}`;
  // `followOn` (#590): the batch to queue once this one is acknowledged, kept
  // on the note until this batch settles. `writes.js`'s `releaseFollowOn`.
  const bare = followOn ? {
    id, paths, subject, batchId, at: Date.now(), followOn,
  } : {
    id, paths, subject, batchId, at: Date.now(),
  };
  const record = batch ? { ...bare, batch } : bare;
  const s = store();
  if (s) {
    try {
      // A cap, because this grows while offline and nothing bounds how long
      // offline lasts. Past the cap the count stops being exact — better than a
      // quota exception on the write path, which would take the save down.
      if (countRecords() < MAX_RECORDS) setRecord(s, record, bare);
    } catch {
      memoryFallback = memoryFallback || new Map();
      memoryFallback.set(id, record);
    }
  } else if (memoryFallback) {
    memoryFallback.set(id, record);
  }
  announce();
  return id;
}

/**
 * Store a note, and if its copy of the batch will not fit, store it without.
 *
 * A large batch can meet the quota where a bare note would not, and the copy is
 * the part that can go: without it the note still counts, still scopes, and is
 * still reported by the #167 check if its page dies early. Falling to the
 * memory map instead would cost every later note its durability. Said once per
 * batch, because #585's recovery is then unavailable for it.
 */
function setRecord(s, record, bare) {
  try {
    s.setItem(`${KEY_PREFIX}${record.id}`, JSON.stringify(record));
  } catch (err) {
    if (record === bare) throw err;
    s.setItem(`${KEY_PREFIX}${record.id}`, JSON.stringify(bare));
    console.warn(
      `[sync] the note for batch ${record.batchId} was stored without its copy of the write `
      + `(${err?.name || 'error'}), so a page closed before the queue stores it cannot `
      + 'resend it (#585).',
    );
  }
}

/**
 * The queue's own record of this batch is durable, so the note's copy can go.
 *
 * Rewrites the note without `batch` and leaves everything else — the note is
 * still the chip's count and #167's durable half until the batch settles.
 * Returns how many notes it rewrote. Silent about failure: a copy left behind
 * costs space, never a write, and `recoverUnqueuedWrites` finds the batch in
 * the queue and drops it again.
 */
export function releaseBatchCopy(batchId) {
  if (!batchId) return 0;
  let released = 0;
  outstandingWrites()
    .filter((record) => record.batchId === batchId && record.batch)
    .forEach((record) => {
      const bare = { ...record };
      delete bare.batch;
      const s = store();
      if (s) {
        try {
          s.setItem(`${KEY_PREFIX}${record.id}`, JSON.stringify(bare));
          released += 1;
        } catch { /* see above */ }
      } else if (memoryFallback?.has(record.id)) {
        memoryFallback.set(record.id, bare);
        released += 1;
      }
    });
  return released;
}

/**
 * Take the follow-on batches off this batch's notes, and hand them back (#590).
 *
 * Rewrites each note without `followOn` and returns what was there, so that
 * whoever is settling the batch decides what happens to them and nobody else
 * can: a second tab settling the same batch finds nothing. Synchronous, like
 * every store here.
 */
export function detachFollowOn(batchId) {
  if (!batchId) return [];
  const taken = [];
  outstandingWrites()
    .filter((record) => record.batchId === batchId && record.followOn)
    .forEach((record) => {
      const rest = { ...record };
      delete rest.followOn;
      const s = store();
      if (s) {
        try {
          s.setItem(`${KEY_PREFIX}${record.id}`, JSON.stringify(rest));
        } catch { return; }
      } else if (memoryFallback?.has(record.id)) {
        memoryFallback.set(record.id, rest);
      } else return;
      taken.push(record.followOn);
    });
  return taken;
}

/**
 * The server has answered the batch this note was written for, either way.
 *
 * ── The pairing is stored rather than remembered (#382) ────────────────────
 *
 * The queue's batches outlive the tab, so after save offline, reload, back
 * online, the batch is replayed by a tab that never enqueued it. A module-scope
 * map from batch id to note id would miss, and the note would never be
 * cleared. So the note carries the batch id itself, and either tab can settle
 * it.
 *
 * **A note with no `batchId` is not matched by anything**: a `null === null`
 * match would have one settled batch sweep away every such note. They are
 * left for the #167 sweep.
 *
 * Returns how many were cleared, so a caller can tell nothing-to-do from done.
 */
export function clearWriteForBatch(batchId) {
  if (!batchId) return 0;
  const ids = outstandingWrites()
    .filter((record) => record.batchId === batchId)
    .map((record) => record.id);
  ids.forEach(clearWrite);
  return ids.length;
}

/** The server has answered this write, either way. */
export function clearWrite(id) {
  if (!id) return;
  const s = store();
  if (s) {
    try {
      s.removeItem(`${KEY_PREFIX}${id}`);
    } catch { /* nothing useful to do; the sweep below will catch it */ }
  }
  if (memoryFallback) memoryFallback.delete(id);
  announce();
}

function countRecords() {
  const s = store();
  if (!s) return memoryFallback ? memoryFallback.size : 0;
  let n = 0;
  try {
    for (let i = 0; i < s.length; i++) {
      if (String(s.key(i)).startsWith(KEY_PREFIX)) n++;
    }
  } catch { return 0; }
  return n;
}

/**
 * Every write still unanswered, oldest first.
 *
 * Reads the store on every call rather than caching, because another tab may
 * have added or cleared one and the `storage` event that tells us arrives after
 * the change is already visible here.
 */
export function outstandingWrites() {
  const s = store();
  if (!s) return memoryFallback ? [...memoryFallback.values()].sort((a, b) => a.at - b.at) : [];
  const out = [];
  try {
    for (let i = 0; i < s.length; i++) {
      const key = String(s.key(i));
      if (!key.startsWith(KEY_PREFIX) || key === `${KEY_PREFIX}probe`) continue;
      try {
        const parsed = JSON.parse(s.getItem(key));
        if (parsed && parsed.id) out.push(parsed);
      } catch {
        // A record we cannot read is a record we cannot act on. Drop it rather
        // than letting one corrupt entry make the count wrong forever.
        s.removeItem(key);
      }
    }
  } catch { return []; }
  return out.sort((a, b) => a.at - b.at);
}

/** How many writes are outstanding. The chip's number. */
export function outstandingCount() {
  return outstandingWrites().length;
}

/**
 * Whose studio a note's writes are for, or `null` if it does not say.
 *
 * Every path this app writes is `users/{uid}/{collection}/{id}`, and a note
 * covers one batch, which never spans two studios. So disagreement answers
 * `null` rather than a guess, as `writes.js`'s `studioOf` does for the batch.
 * The two are not shared: this file imports nothing, so it can be the
 * synchronous thing written before the mutation.
 */
export function studioOfWrite(record) {
  let found = null;
  for (const path of (record?.paths || [])) {
    const uid = String(path || '').split('/')[1] || null;
    if (!uid) return null;
    if (found === null) found = uid;
    else if (found !== uid) return null;
  }
  return found;
}

/**
 * Every unanswered write that is **this teacher's**, oldest first.
 *
 * ── The notes are scoped rather than cleared at sign-out (#282) ────────────
 *
 * These records are per *origin*, so two teachers on one laptop share them.
 * The write queue beside this **holds** a batch whose studio is not the
 * signed-in one (`writes.js` parks it), so it goes out when that teacher signs
 * back in; deleting the notes would lose the durable half of #167 for work
 * still owed. So the note stays and the *count* is scoped.
 *
 * **A note that does not say whose it is counts for everybody**
 * (`writes.js` always carries the refs), so the error is over-reporting.
 */
export function outstandingWritesFor(uid) {
  return outstandingWrites().filter((record) => {
    const studio = studioOfWrite(record);
    return studio === null || studio === uid;
  });
}

/** How many of this teacher's writes are outstanding. The chip's number. */
export function outstandingCountFor(uid) {
  return outstandingWritesFor(uid).length;
}

/**
 * Forget every record.
 *
 * No caller in `src/`. `SyncContext`'s #167 check clears the records it
 * reported, by id: every note would include writes the tab is still holding a
 * promise for (#278). A sign-out parks the previous teacher's work rather than
 * destroying it (#282, #283), so it scopes the count with
 * `outstandingWritesFor(uid)` instead. This is a "forget everything on this
 * device".
 */
export function clearAllWrites() {
  const s = store();
  if (s) {
    try {
      const keys = [];
      for (let i = 0; i < s.length; i++) {
        const key = String(s.key(i));
        if (key.startsWith(KEY_PREFIX)) keys.push(key);
      }
      keys.forEach((k) => s.removeItem(k));
    } catch { /* nothing useful to do */ }
  }
  if (memoryFallback) memoryFallback.clear();
  announce();
}

/**
 * Be told when the set of outstanding writes changes, in this tab or another.
 *
 * The `storage` event fires only in *other* tabs, which is why our own mutators
 * announce directly as well. Returns its own unsubscribe.
 */
export function onOutstandingChange(listener) {
  listeners.add(listener);
  const onStorage = (e) => {
    if (!e || !e.key || String(e.key).startsWith(KEY_PREFIX)) listener();
  };
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
}

/** Test seam. Resets the in-memory degradation so a fresh probe runs. */
export function __resetOutstandingWritesForTests() {
  memoryFallback = null;
  probed = false;
  listeners.clear();
}
